import type { PayloadAction } from '@reduxjs/toolkit'

import type { PickerSource } from 'slices/aiConversations/pickerTypes'

import type {
  EndpointConfig,
  GlobalConfig,
  OperatorOption,
  PickerSliceState,
} from './types'
import {
  createInitialGlobalConfig,
  DEFAULT_GROUP_ID,
  ensureGroup,
} from './types'

// ═══════════════════════════════════════════════════════════════════════════
// Reducer Cases
// ═══════════════════════════════════════════════════════════════════════════

export const globalConfigReducers = {
  /**
   * Set the entire config for a group.
   */
  setGroupConfig: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; config: GlobalConfig }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, config } = action.payload
    const group = ensureGroup(state, groupId)
    group.config = config
  },

  /**
   * Set level 1 config for a group.
   */
  setGroupLevel1Config: (
    state: PickerSliceState,
    action: PayloadAction<{
      groupId?: string
      id?: string
      label?: string
      operatorOptions?: OperatorOption[]
      endpointConfig?: EndpointConfig | null
      keyField?: string
      valueField?: string
      sources?: PickerSource[]
      defaultSource?: string | null
    }>
  ) => {
    const {
      groupId = DEFAULT_GROUP_ID,
      id,
      label,
      operatorOptions,
      endpointConfig,
      keyField,
      valueField,
      sources,
      defaultSource,
    } = action.payload
    const group = ensureGroup(state, groupId)
    if (id !== undefined) {
      group.config.level1.id = id
    }
    if (label !== undefined) {
      group.config.level1.label = label
    }
    if (operatorOptions !== undefined) {
      group.config.level1.operatorOptions = operatorOptions
    }
    if (endpointConfig !== undefined) {
      group.config.level1.endpointConfig = endpointConfig
    }
    if (keyField !== undefined) {
      group.config.level1.keyField = keyField
    }
    if (valueField !== undefined) {
      group.config.level1.valueField = valueField
    }
    if (sources !== undefined) {
      group.config.level1.sources = sources
    }
    if (defaultSource !== undefined) {
      group.config.level1.defaultSource = defaultSource
    }
  },

  /**
   * Set level 2 config for a group.
   */
  setGroupLevel2Config: (
    state: PickerSliceState,
    action: PayloadAction<{
      groupId?: string
      id?: string
      label?: string
      operatorOptions?: OperatorOption[]
      endpointConfig?: EndpointConfig | null
      subtitle?: string | null
      keyField?: string
      valueField?: string
      sources?: PickerSource[]
      defaultSource?: string | null
    }>
  ) => {
    const {
      groupId = DEFAULT_GROUP_ID,
      id,
      label,
      operatorOptions,
      endpointConfig,
      subtitle,
      keyField,
      valueField,
      sources,
      defaultSource,
    } = action.payload
    const group = ensureGroup(state, groupId)
    if (id !== undefined) {
      group.config.level2.id = id
    }
    if (label !== undefined) {
      group.config.level2.label = label
    }
    if (operatorOptions !== undefined) {
      group.config.level2.operatorOptions = operatorOptions
    }
    if (endpointConfig !== undefined) {
      group.config.level2.endpointConfig = endpointConfig
    }
    if (subtitle !== undefined) {
      group.config.level2.subtitle = subtitle
    }
    if (keyField !== undefined) {
      group.config.level2.keyField = keyField
    }
    if (valueField !== undefined) {
      group.config.level2.valueField = valueField
    }
    if (sources !== undefined) {
      group.config.level2.sources = sources
    }
    if (defaultSource !== undefined) {
      group.config.level2.defaultSource = defaultSource
    }
  },

  /**
   * Reset a group's config to defaults.
   */
  resetGroupConfig: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID } = action.payload
    const group = ensureGroup(state, groupId)
    group.config = createInitialGlobalConfig()
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Group-Level Actions (pickerId, topOperator, disabled, title, subtitle)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Set the picker_id for a group (from server response).
   */
  setGroupPickerId: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; pickerId: string }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, pickerId } = action.payload
    const group = ensureGroup(state, groupId)
    group.pickerId = pickerId
  },

  setGroupTopOperator: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; operator: string }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, operator } = action.payload
    const group = ensureGroup(state, groupId)
    group.topOperator = operator
  },

  setGroupDisabled: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; disabled: boolean }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, disabled } = action.payload
    const group = ensureGroup(state, groupId)
    group.disabled = disabled
  },

  setGroupTitle: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; title: string }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, title } = action.payload
    const group = ensureGroup(state, groupId)
    group.title = title
  },

  setGroupSubtitle: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; subtitle: string | null }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, subtitle } = action.payload
    const group = ensureGroup(state, groupId)
    group.subtitle = subtitle
  },

  setGroupSingleLevel: (
    state: PickerSliceState,
    action: PayloadAction<{ groupId?: string; singleLevel: boolean }>
  ) => {
    const { groupId = DEFAULT_GROUP_ID, singleLevel } = action.payload
    const group = ensureGroup(state, groupId)
    group.singleLevel = singleLevel
  },
}

// ═══════════════════════════════════════════════════════════════════════════
// Selectors
// ═══════════════════════════════════════════════════════════════════════════

type RootState = { picker: PickerSliceState }

const defaultGlobalConfig = createInitialGlobalConfig()

/**
 * Helper to get group ID for a picker.
 */
const getGroupIdForPicker = (
  state: RootState,
  pickerId: number
): string | undefined => {
  return state.picker.pickerToGroup[pickerId]
}

/**
 * Helper to get group config for a picker.
 */
const getGroupConfigForPicker = (
  state: RootState,
  pickerId: number
): GlobalConfig => {
  const groupId = getGroupIdForPicker(state, pickerId)
  if (!groupId) return defaultGlobalConfig
  return state.picker.groups[groupId]?.config ?? defaultGlobalConfig
}

/**
 * Get the full config for a picker's group.
 */
export const selectGroupConfig = (
  state: RootState,
  pickerId: number
): GlobalConfig => getGroupConfigForPicker(state, pickerId)

// ═══════════════════════════════════════════════════════════════════════════
// Group-level selectors (using groupId directly)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Helper to get group config by groupId.
 */
const getGroupConfigByGroupId = (
  state: RootState,
  groupId: string
): GlobalConfig => {
  return state.picker.groups[groupId]?.config ?? defaultGlobalConfig
}

export const selectGroupPickerId = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): string => state.picker.groups[groupId]?.pickerId ?? ''

export const selectGroupTopOperator = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): string => state.picker.groups[groupId]?.topOperator ?? 'any'

export const selectGroupDisabled = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): boolean => state.picker.groups[groupId]?.disabled ?? false

export const selectGroupTitle = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): string => state.picker.groups[groupId]?.title ?? ''

export const selectGroupSubtitle = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): string | null => state.picker.groups[groupId]?.subtitle ?? null

export const selectGroupSingleLevel = (
  state: RootState,
  groupId: string = DEFAULT_GROUP_ID
): boolean => state.picker.groups[groupId]?.singleLevel ?? false

// Config selectors by groupId (for use in group-level components)
export const selectGroupLevel1Id = (
  state: RootState,
  groupId: string
): string => getGroupConfigByGroupId(state, groupId).level1.id

export const selectGroupLevel2Id = (
  state: RootState,
  groupId: string
): string => getGroupConfigByGroupId(state, groupId).level2.id

export const selectGroupLevel1Label = (
  state: RootState,
  groupId: string
): string => getGroupConfigByGroupId(state, groupId).level1.label

export const selectGroupLevel2Label = (
  state: RootState,
  groupId: string
): string => getGroupConfigByGroupId(state, groupId).level2.label

export const selectGroupLevelConfig = (
  state: RootState,
  groupId: string,
  level: 'level1' | 'level2'
): LevelConfig => getGroupConfigByGroupId(state, groupId)[level]

export const selectGroupLevel1OperatorOptions = (
  state: RootState,
  groupId: string
): OperatorOption[] =>
  getGroupConfigByGroupId(state, groupId).level1.operatorOptions

export const selectGroupLevel2OperatorOptions = (
  state: RootState,
  groupId: string
): OperatorOption[] =>
  getGroupConfigByGroupId(state, groupId).level2.operatorOptions

// ═══════════════════════════════════════════════════════════════════════════
// Active source
// ═══════════════════════════════════════════════════════════════════════════

type LevelConfig = GlobalConfig['level1'] | GlobalConfig['level2']

/** The source named as the level's default, when it has one by that name */
const activeSource = (level: LevelConfig): PickerSource | undefined =>
  level.sources.find(source => source.name === level.defaultSource)

export const SPECIFIC_SOURCE = 'specific'

/** A level's self-updating default source and the fixed list it can switch to */
export interface SourceChoice {
  smart: PickerSource
  specific: PickerSource
}

/** null for a level that offers no choice of source */
export const sourceChoiceOf = ({
  sources = [],
  defaultSource,
}: {
  sources?: PickerSource[]
  defaultSource?: string | null
}): SourceChoice | null => {
  const smart = sources.find(source => source.name === defaultSource)
  const specific = sources.find(source => source.name === SPECIFIC_SOURCE)
  return smart && specific && smart !== specific ? { smart, specific } : null
}

// One EndpointConfig per stored source, so useSelector gets the same object back.
const endpointConfigs = new WeakMap<PickerSource, EndpointConfig>()

const activeEndpointConfig = (level: LevelConfig): EndpointConfig | null => {
  const source = activeSource(level)
  if (!source) return level.endpointConfig

  const cached = endpointConfigs.get(source)
  if (cached) return cached
  const config = { endpoint: source.endpoint, params: source.params }
  endpointConfigs.set(source, config)
  return config
}

// ═══════════════════════════════════════════════════════════════════════════
// Level 1 selectors (using pickerId -> pickerToGroup mapping)
// ═══════════════════════════════════════════════════════════════════════════

export const selectLevel1Id = (state: RootState, pickerId: number): string =>
  getGroupConfigForPicker(state, pickerId).level1.id

export const selectLevel1Label = (state: RootState, pickerId: number): string =>
  getGroupConfigForPicker(state, pickerId).level1.label

export const selectLevel1OperatorOptions = (
  state: RootState,
  pickerId: number
): OperatorOption[] =>
  getGroupConfigForPicker(state, pickerId).level1.operatorOptions

export const selectLevel1EndpointConfig = (
  state: RootState,
  pickerId: number
): EndpointConfig | null =>
  activeEndpointConfig(getGroupConfigForPicker(state, pickerId).level1)

export const selectLevel1KeyField = (
  state: RootState,
  pickerId: number
): string => {
  const level = getGroupConfigForPicker(state, pickerId).level1
  return (activeSource(level) ?? level).keyField
}

export const selectLevel1ValueField = (
  state: RootState,
  pickerId: number
): string => {
  const level = getGroupConfigForPicker(state, pickerId).level1
  return (activeSource(level) ?? level).valueField
}

// ═══════════════════════════════════════════════════════════════════════════
// Level 2 selectors (using pickerId -> pickerToGroup mapping)
// ═══════════════════════════════════════════════════════════════════════════

export const selectLevel2Id = (state: RootState, pickerId: number): string =>
  getGroupConfigForPicker(state, pickerId).level2.id

export const selectLevel2Label = (state: RootState, pickerId: number): string =>
  getGroupConfigForPicker(state, pickerId).level2.label

export const selectLevel2OperatorOptions = (
  state: RootState,
  pickerId: number
): OperatorOption[] =>
  getGroupConfigForPicker(state, pickerId).level2.operatorOptions

export const selectLevel2EndpointConfig = (
  state: RootState,
  pickerId: number
): EndpointConfig | null =>
  activeEndpointConfig(getGroupConfigForPicker(state, pickerId).level2)

export const selectLevel2Subtitle = (
  state: RootState,
  pickerId: number
): string | null => getGroupConfigForPicker(state, pickerId).level2.subtitle

export const selectLevel2KeyField = (
  state: RootState,
  pickerId: number
): string => {
  const level = getGroupConfigForPicker(state, pickerId).level2
  return (activeSource(level) ?? level).keyField
}

export const selectLevel2ValueField = (
  state: RootState,
  pickerId: number
): string => {
  const level = getGroupConfigForPicker(state, pickerId).level2
  return (activeSource(level) ?? level).valueField
}

export const selectSingleLevel = (
  state: RootState,
  pickerId: number
): boolean => {
  const groupId = getGroupIdForPicker(state, pickerId)
  if (!groupId) return false
  return state.picker.groups[groupId]?.singleLevel ?? false
}

// ═══════════════════════════════════════════════════════════════════════════
// Legacy aliases (for backward compatibility - these are deprecated)
// ═══════════════════════════════════════════════════════════════════════════

/** @deprecated Use selectLevel1Label with pickerId instead */
export const selectGlobalLevel1Label = (_state: RootState): string =>
  defaultGlobalConfig.level1.label

/** @deprecated Use selectLevel1OperatorOptions with pickerId instead */
export const selectGlobalLevel1OperatorOptions = (
  _state: RootState
): OperatorOption[] => defaultGlobalConfig.level1.operatorOptions

/** @deprecated Use selectLevel1EndpointConfig with pickerId instead */
export const selectGlobalLevel1EndpointConfig = (
  _state: RootState
): EndpointConfig | null => defaultGlobalConfig.level1.endpointConfig

/** @deprecated Use selectLevel1KeyField with pickerId instead */
export const selectGlobalLevel1KeyField = (_state: RootState): string =>
  defaultGlobalConfig.level1.keyField

/** @deprecated Use selectLevel1ValueField with pickerId instead */
export const selectGlobalLevel1ValueField = (_state: RootState): string =>
  defaultGlobalConfig.level1.valueField

/** @deprecated Use selectLevel2Label with pickerId instead */
export const selectGlobalLevel2Label = (_state: RootState): string =>
  defaultGlobalConfig.level2.label

/** @deprecated Use selectLevel2OperatorOptions with pickerId instead */
export const selectGlobalLevel2OperatorOptions = (
  _state: RootState
): OperatorOption[] => defaultGlobalConfig.level2.operatorOptions

/** @deprecated Use selectLevel2EndpointConfig with pickerId instead */
export const selectGlobalLevel2EndpointConfig = (
  _state: RootState
): EndpointConfig | null => defaultGlobalConfig.level2.endpointConfig

/** @deprecated Use selectLevel2Subtitle with pickerId instead */
export const selectGlobalLevel2Subtitle = (_state: RootState): string | null =>
  defaultGlobalConfig.level2.subtitle

/** @deprecated Use selectLevel2KeyField with pickerId instead */
export const selectGlobalLevel2KeyField = (_state: RootState): string =>
  defaultGlobalConfig.level2.keyField

/** @deprecated Use selectLevel2ValueField with pickerId instead */
export const selectGlobalLevel2ValueField = (_state: RootState): string =>
  defaultGlobalConfig.level2.valueField

/** @deprecated Use selectGroupTopOperator with groupId instead */
export const selectGlobalTopOperator = (state: RootState): string =>
  selectGroupTopOperator(state, DEFAULT_GROUP_ID)

/** @deprecated Use selectGroupConfig with pickerId instead */
export const selectGlobalConfig = (_state: RootState): GlobalConfig =>
  defaultGlobalConfig
